// ACTA_KANBAN_POINTER_DRAG_SIMPLE_FILTER_V14
// ACTA_QUOTE_FINANCING_DECISION_UI_V1
// ACTA_QUOTES_COLUMNS_PIPELINE_STYLE_V1
// ACTA_QUOTES_MOBILE_COMPACT_V1
// ACTA_QUOTES_NO_PURCHASE_ORDER_UI_V1
// ACTA_QUOTES_LEGACY_STATUS_COMPATIBILITY_V1
import { listProspects } from "@/api/prospects";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CaretDown,
  CheckCircle,
  CurrencyEur,
  FileText,
  MagnifyingGlass,
  PaperPlaneTilt,
  PlusCircle,
  XCircle,
} from "@phosphor-icons/react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { quotes as quotesApi } from "@/api";
import ShareModal from "@/components/ShareModal";
import QuoteCreateForm from "@/components/QuoteCreateForm";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const STATUS_LABELS = {
  draft: "Brouillon",
  pending_validation: "Validation admin",
  approved: "Validé admin",
  sent: "Partagé client",
  accepted: "Accepté",
  rejected: "Rejeté admin",
  refused: "Refusé client",
};

const QUOTE_COLUMNS = [
  {
    id: "draft",
    title: "Brouillon",
    description: "Préparation",
    statuses: ["draft"],
    dot: "bg-[var(--do-accent-soft)]0",
  },
  {
    id: "pending_validation",
    title: "Validation admin",
    description: "À valider",
    statuses: ["pending_validation"],
    dot: "bg-[var(--do-accent-soft)]0",
  },
  {
    id: "approved",
    title: "Validé admin",
    description: "PDF + partage",
    statuses: ["approved"],
    dot: "bg-[var(--do-accent-soft)]0",
  },
  {
    id: "sent",
    title: "Partagé client",
    description: "Retour client",
    statuses: ["sent"],
    dot: "bg-[var(--do-accent-soft)]0",
  },
  {
    id: "accepted",
    title: "Accepté · financement",
    description: "CA provisoire",
    statuses: ["accepted"],
    dot: "bg-[var(--do-accent-soft)]0",
  },
  {
    id: "closed",
    title: "Refusé / rejeté",
    description: "Sortie workflow",
    statuses: ["rejected", "refused"],
    dot: "bg-[var(--do-accent-soft)]0",
  },
];

const QUOTE_COLUMN_PAGE_SIZE = 5;
const QUOTE_TOUCH_DRAG_DELAY = 320;
const QUOTE_TOUCH_MOVE_TOLERANCE = 10;
const QUOTE_DROP_COLUMNS = new Set([
  "draft",
  "pending_validation",
  "approved",
  "sent",
  "accepted",
  "closed",
]);
const PDF_ALLOWED_STATUSES = new Set([
  "approved",
  "sent",
  "accepted",
  "refused",
]);

function normalizeQuoteForUi(quote) {
  if (
    quote?.status
    === "purchase_order_created"
  ) {
    return {
      ...quote,
      status: "accepted",
      legacyStatus:
        "purchase_order_created",
    };
  }

  return quote;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function quoteFinancingStatus(quote) {
  if (
    quote?.legacyStatus ===
    "purchase_order_created"
  ) {
    return "legacy";
  }

  if (quote?.financingStatus) {
    return quote.financingStatus;
  }

  return quote?.status === "accepted"
    ? "pending"
    : "";
}


function quoteBelongsToColumn(
  quote,
  column
) {
  const financingStatus =
    quoteFinancingStatus(quote);

  if (column.id === "accepted") {
    return (
      quote.status === "accepted" &&
      ![
        "refused",
        "cancelled",
      ].includes(financingStatus)
    );
  }

  if (column.id === "closed") {
    return (
      column.statuses.includes(
        quote.status || "draft"
      ) ||
      (
        quote.status === "accepted" &&
        [
          "refused",
          "cancelled",
        ].includes(financingStatus)
      )
    );
  }

  return column.statuses.includes(
    quote.status || "draft"
  );
}


function quoteColumnId(quote) {
  const matchingColumn = QUOTE_COLUMNS.find((column) =>
    quoteBelongsToColumn(quote, column)
  );
  return matchingColumn?.id || "draft";
}


function quoteDropRequirement(quote, nextColumn, isAdmin) {
  const currentColumn = quoteColumnId(quote);
  if (currentColumn === nextColumn) return null;

  if (
    nextColumn === "pending_validation"
    && (quote.status === "draft" || quote.status === "rejected")
  ) {
    return {
      blocked: false,
      title: "Soumettre à la validation",
      description:
        "Le backend vérifiera que le devis est complet avant de le transmettre à l’administrateur.",
      actionType: "submit",
      actionLabel: "Soumettre",
    };
  }

  if (nextColumn === "approved" && quote.status === "pending_validation") {
    return isAdmin
      ? {
          blocked: false,
          title: "Contrôler puis valider",
          description:
            "Prévisualisez le PDF avant la validation administrative.",
          actionType: "approve",
          actionLabel: "Valider le devis",
          canPreview: true,
        }
      : {
          blocked: true,
          title: "Validation administrative requise",
          description: "Ce devis attend la décision d’un administrateur.",
          actionType: "none",
          actionLabel: "",
        };
  }

  if (nextColumn === "sent" && quote.status === "approved") {
    return {
      blocked: false,
      title: "Partager au client",
      description: "Le statut changera uniquement après un partage réussi.",
      actionType: "share",
      actionLabel: "Préparer le partage",
    };
  }

  if (nextColumn === "accepted" && quote.status === "sent") {
    return {
      blocked: false,
      title: "Enregistrer l’acceptation",
      description: "Confirmez que le client a accepté ce devis.",
      actionType: "accept",
      actionLabel: "Confirmer l’acceptation",
    };
  }

  if (nextColumn === "closed") {
    if (quote.status === "pending_validation" && isAdmin) {
      return {
        blocked: false,
        title: "Rejeter le devis",
        description: "Un motif administratif sera obligatoire.",
        actionType: "admin_reject",
        actionLabel: "Indiquer le motif",
      };
    }
    if (quote.status === "sent") {
      return {
        blocked: false,
        title: "Enregistrer le refus client",
        description: "Le motif communiqué par le client sera conservé.",
        actionType: "client_refuse",
        actionLabel: "Indiquer le motif",
      };
    }
    if (quote.status === "accepted" && isAdmin) {
      return {
        blocked: false,
        title: "Refuser le financement",
        description:
          "Cette décision nécessite un motif et ne confirme pas le chiffre d’affaires.",
        actionType: "financing_refuse",
        actionLabel: "Indiquer le motif",
      };
    }
  }

  if (
    nextColumn === "draft"
    && ["rejected", "pending_validation"].includes(quote.status)
  ) {
    return {
      blocked: false,
      title: "Corriger le devis",
      description:
        "Modifiez les éléments demandés avant une nouvelle soumission.",
      actionType: "edit",
      actionLabel: "Compléter le devis",
    };
  }

  return {
    blocked: true,
    title: "Étape non disponible",
    description: "Le devis doit suivre les étapes du workflow dans l’ordre.",
    actionType: "none",
    actionLabel: "",
  };
}


function QuoteDropActionModal({
  action,
  saving,
  onClose,
  onConfirm,
  onPreview,
}) {
  if (!action) return null;

  return (
    <div
      className="acta-modal-overlay fixed inset-0 z-[95] flex items-center justify-center p-3 sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        className="acta-modal-surface w-full max-w-md rounded-3xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]"
        data-testid="quote-drop-action-modal"
      >
        <div
          className={`rounded-2xl border p-4 ${
            action.requirement.blocked
              ? "border-[var(--do-border)] bg-[var(--do-accent-soft)]"
              : "border-[var(--do-border)] bg-[var(--do-accent-soft)]"
          }`}
        >
          <div className="text-xs font-black uppercase tracking-[0.1em] text-[var(--do-muted)]">
            {action.quote.quoteNumber || action.quote.quote_number || action.quote.id}
          </div>
          <h2 className="mt-1 font-heading text-lg font-black text-[var(--do-ink)]">
            {action.requirement.title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-[var(--do-muted)]">
            {action.requirement.description}
          </p>
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] disabled:opacity-50"
          >
            Fermer
          </button>

          {(action.requirement.canPreview || action.requirement.actionType === "submit") && (
            <button
              type="button"
              onClick={onPreview}
              disabled={saving}
              className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 text-sm font-black text-[var(--do-accent)] disabled:opacity-50"
            >
              Prévisualiser
            </button>
          )}

          {action.requirement.actionType !== "none" && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={saving}
              className="min-h-[44px] rounded-xl bg-[var(--do-accent-strong)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
              data-testid="quote-drop-action-cta"
            >
              {action.requirement.actionLabel}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}


function statusClass(status) {
  if (status === "approved" || status === "accepted") {
    return "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]";
  }

  if (status === "pending_validation") {
    return "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]";
  }

  if (status === "rejected" || status === "refused") {
    return "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]";
  }

  if (status === "sent") {
    return "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]";
  }

  return "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-muted)]";
}

function quoteSearchHaystack(quote) {
  return [
    quote?.quoteNumber,
    quote?.quote_number,
    quote?.customerName,
    quote?.prospectName,
    quote?.clientName,
    quote?.company,
    quote?.title,
    quote?.machineName,
    quote?.machine_name,
    quote?.status,
    STATUS_LABELS[quote?.status],
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("fr-FR");
}

function MetricCard({
  label,
  value,
  icon: Icon,
  onClick,
  active = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-[18px] border bg-[var(--do-bg-elev)] p-2.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md sm:rounded-[22px] sm:p-4 ${
        active
          ? "border-[var(--do-border)] ring-2 ring-[var(--do-accent)]"
          : "border-[var(--do-border)]"
      }`}
      data-testid="quote-metric-filter"
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <div className="line-clamp-2 font-heading text-[10px] font-semibold uppercase leading-tight tracking-[0.08em] text-[var(--do-muted)] sm:text-xs sm:tracking-[0.12em]">
            {label}
          </div>

          <div className="mt-1 font-heading text-lg font-black leading-none text-[var(--do-ink)] sm:mt-2 sm:text-2xl">
            {value}
          </div>
        </div>

        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent-soft)] text-[var(--do-accent)] sm:h-10 sm:w-10 sm:rounded-2xl">
          <Icon size={18} weight="bold" />
        </div>
      </div>
    </button>
  );
}

function LegacyCommerceQuotes() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";

  // ACTA_QUOTE_REJECTED_EDIT_FRONT_V1
  const canEditWorkflowQuote = (quote) => {
    if (
      !quote
      || !["rejected", "pending_validation"].includes(quote.status)
    ) return false;
    return (
      isAdmin ||
      quote.createdBy === user?.id ||
      quote.sellerUserId === user?.id
    );
  };

  const [editingQuote, setEditingQuote] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    machine_name: "",
    quantity: "1",
    amount: "",
    notes: "",
  });

  const [decisionModal, setDecisionModal] = useState(null);
  const [decisionReason, setDecisionReason] = useState("");
  const [decisionSubmitting, setDecisionSubmitting] = useState(false);
  const [
    decisionPrepareGmao,
    setDecisionPrepareGmao,
  ] = useState(true);

  const openEditWorkflowQuote = (quote) => {
    if (!canEditWorkflowQuote(quote)) return;

    setEditForm({
      title: quote.title || "Devis commercial",
      machine_name:
        quote.machineName ||
        quote.machine_name ||
        quote.productName ||
        "",
      quantity: String(quote.quantity || 1),
      amount: String(quote.amount ?? ""),
      notes: quote.notes || "",
    });

    setEditingQuote(quote);
  };

  const closeEditWorkflowQuote = () => {
    if (editSubmitting) return;
    setEditingQuote(null);
  };

  const updateEditForm = (key, value) => {
    setEditForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const submitEditWorkflowQuote = async (event) => {
    event.preventDefault();

    if (!editingQuote) return;

    const quantity = Number(editForm.quantity);
    const amount = Number(editForm.amount);

    if (!editForm.title.trim()) {
      toast.error("Le titre du devis est obligatoire.");
      return;
    }

    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error("La quantité doit être supérieure à zéro.");
      return;
    }

    if (!Number.isFinite(amount) || amount < 0) {
      toast.error("Le montant HT est invalide.");
      return;
    }

    setEditSubmitting(true);

    try {
      await quotesApi.update(editingQuote.id, {
        title: editForm.title.trim(),
        machine_name: editForm.machine_name.trim(),
        quantity,
        amount,
        notes: editForm.notes.trim(),
      });

      toast.success("Devis modifié.");
      setEditingQuote(null);
      await load();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
        error.message ||
        "Impossible de modifier ce devis."
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  const openDecisionModal = (quote, type) => {
    setDecisionReason("");
    setDecisionPrepareGmao(
      type === "financing_approve"
    );
    setDecisionModal({
      quote,
      type,
    });
  };

  const closeDecisionModal = () => {
    if (decisionSubmitting) return;
    setDecisionModal(null);
    setDecisionReason("");
    setDecisionPrepareGmao(true);
  };

  const submitDecision = async (event) => {
    event.preventDefault();

    if (!decisionModal) return;

    const reason = decisionReason.trim();
    const isFinancingApproval =
      decisionModal.type ===
      "financing_approve";
    const needsReason =
      !isFinancingApproval;

    if (needsReason && !reason) {
      toast.error("Le motif est obligatoire.");
      return;
    }

    setDecisionSubmitting(true);

    try {
      if (
        decisionModal.type ===
        "admin_reject"
      ) {
        await quotesApi.reject(
          decisionModal.quote.id,
          reason
        );
        toast.success("Devis rejeté.");
      } else if (
        decisionModal.type ===
        "client_refuse"
      ) {
        await quotesApi.refuse(
          decisionModal.quote.id,
          reason
        );
        toast.success("Devis refusé.");
      } else {
        const financingDecision = {
          financing_approve:
            "approved",
          financing_refuse:
            "refused",
          financing_cancel:
            "cancelled",
        }[decisionModal.type];

        await quotesApi.decideFinancing(
          decisionModal.quote.id,
          {
            decision:
              financingDecision,
            reason,
            prepareGmao:
              isFinancingApproval &&
              decisionPrepareGmao,
          }
        );

        const successMessages = {
          approved:
            decisionPrepareGmao
              ? (
                "Financement accepté. "
                + "CA confirmé et transfert "
                + "GMAO préparé."
              )
              : (
                "Financement accepté. "
                + "CA confirmé sans transfert GMAO."
              ),
          refused:
            "Financement refusé.",
          cancelled:
            "Dossier de financement annulé.",
        };

        toast.success(
          successMessages[
            financingDecision
          ]
        );
      }

      setDecisionModal(null);
      setDecisionReason("");
      setDecisionPrepareGmao(true);
      await load();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        error.message ||
        "Action impossible."
      );
    } finally {
      setDecisionSubmitting(false);
    }
  };


  const [prospects, setProspects] = useState([]);
  const [prospectsLoading, setProspectsLoading] = useState(false);
  const [prospectsError, setProspectsError] = useState("");

  const [quotes, setQuotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [quoteDropAction, setQuoteDropAction] = useState(null);
  const [draggedQuote, setDraggedQuote] = useState(null);
  const [quoteDragOverColumn, setQuoteDragOverColumn] = useState("");
  const quoteDragTimerRef = useRef(null);
  const quoteTouchDragRef = useRef({
    active: false,
    quote: null,
    pointerId: null,
    startX: 0,
    startY: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [quoteSearch, setQuoteSearch] = useState("");
  const [columnPages, setColumnPages] = useState({});
  const [openMobileColumn, setOpenMobileColumn] = useState("draft");
  const [quoteFormOpen, setQuoteFormOpen] = useState(false);

  useEffect(() => {
    const requestedSearch = searchParams.get("search");
    if (requestedSearch) setQuoteSearch(requestedSearch);

    if (searchParams.get("action") !== "new") return;

    setQuoteFormOpen(true);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("action");

    setSearchParams(nextParams, {
      replace: true,
    });
  }, [searchParams, setSearchParams]);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareItem, setShareItem] = useState(null);
  const [shareDefaultProspect, setShareDefaultProspect] = useState(null);

  const [quotePreview, setQuotePreview] = useState(null);

  const totals = summary?.totals || {};

  useEffect(() => {
    let mounted = true;

    async function loadProspects() {
      setProspectsLoading(true);
      setProspectsError("");

      try {
        const response = await listProspects({ limit: 500, lifecycleStage: "" });
        const rows =
          response?.items ||
          response?.prospects ||
          response?.data ||
          (Array.isArray(response) ? response : []);

        if (!mounted) return;

        setProspects(
          rows
            .filter((item) => item?.id)
            .map((item) => ({
              ...item,
              lifecycleStage: item.lifecycleStage || "prospect",
            }))
        );
      } catch (error) {
        console.error("Impossible de charger les prospects/clients", error);
        if (mounted) {
          setProspectsError("Impossible de charger les prospects/clients.");
        }
      } finally {
        if (mounted) {
          setProspectsLoading(false);
        }
      }
    }

    loadProspects();

    return () => {
      mounted = false;
    };
  }, []);

  const load = async () => {
    setLoading(true);

    try {
      const [listData, summaryData] = await Promise.all([
        quotesApi.list({
          /*
           * Le filtre Accepté est appliqué côté front afin
           * d'inclure les anciens devis dont le statut
           * backend est purchase_order_created.
           */
          status:
            statusFilter === "accepted"
              ? ""
              : statusFilter,
          limit: 500,
        }),
        quotesApi.summary(),
      ]);

      setQuotes(
        (listData.items || [])
          .map(normalizeQuoteForUi)
      );

      setSummary(summaryData);
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
        error.message ||
        "Impossible de charger les devis."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    setColumnPages({});
  }, [quoteSearch, statusFilter]);

  const filteredQuotes = useMemo(() => {
    const normalizedSearch =
      quoteSearch
        .trim()
        .toLocaleLowerCase(
          "fr-FR"
        );

    return quotes.filter(
      (quote) => {
        if (
          statusFilter
          && quote.status
            !== statusFilter
        ) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return quoteSearchHaystack(
          quote
        ).includes(
          normalizedSearch
        );
      }
    );
  }, [
    quotes,
    quoteSearch,
    statusFilter,
  ]);

  const groupedQuotes = useMemo(() => {
    return QUOTE_COLUMNS.map((column) => {
      const columnQuotes = filteredQuotes.filter(
        (quote) =>
          quoteBelongsToColumn(
            quote,
            column
          )
      );

      const totalPages = Math.max(
        1,
        Math.ceil(columnQuotes.length / QUOTE_COLUMN_PAGE_SIZE)
      );
      const requestedPage = columnPages[column.id] || 1;
      const page = Math.min(Math.max(requestedPage, 1), totalPages);
      const startIndex = (page - 1) * QUOTE_COLUMN_PAGE_SIZE;
      const endIndex = Math.min(
        startIndex + QUOTE_COLUMN_PAGE_SIZE,
        columnQuotes.length
      );

      return {
        ...column,
        quotes: columnQuotes,
        visibleQuotes: columnQuotes.slice(startIndex, endIndex),
        page,
        totalPages,
        startIndex,
        endIndex,
      };
    });
  }, [filteredQuotes, columnPages]);

  const updateColumnPage = (columnId, direction) => {
    setColumnPages((current) => ({
      ...current,
      [columnId]: Math.max(1, (current[columnId] || 1) + direction),
    }));
  };

  const runAction = async (label, action) => {
    try {
      await action();
      toast.success(label);
      await load();
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
        error.message ||
        "Action impossible."
      );
    }
  };

  const downloadQuotePdf = async (quote) => {
    try {
      const blob = await quotesApi.downloadPdf(quote.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${quote.quoteNumber || quote.quote_number || quote.id || "devis"}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
        "Téléchargement PDF impossible"
      );
    }
  };

  const previewQuotePdf = async (quote) => {
    try {
      const blob = await quotesApi.downloadPdf(
        quote.id,
        { preview: true }
      );
      const url = window.URL.createObjectURL(blob);

      setQuotePreview({
        url,
        title: `${quote.quoteNumber || quote.quote_number || quote.id || "devis"} - ${quote.title || "Devis"}`,
      });
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
        "Prévisualisation PDF impossible"
      );
    }
  };

  const closeQuotePreview = () => {
    if (quotePreview?.url) {
      window.URL.revokeObjectURL(quotePreview.url);
    }

    setQuotePreview(null);
  };

  const openQuoteShare = (quote) => {
    const quoteContactName = (
      quote.customerName ||
      quote.prospectName ||
      quote.clientName ||
      quote.company ||
      ""
    ).trim();

    const linkedProspect = prospects.find((prospect) => {
      const prospectName = `${prospect.firstName || ""} ${prospect.lastName || ""}`.trim();
      const prospectLabel = prospect.company || prospectName || prospect.email || "";

      return (
        prospect.id === quote.prospectId ||
        prospect.id === quote.prospect_id ||
        prospect.id === quote.clientId ||
        prospect.id === quote.client_id ||
        (quoteContactName && prospectLabel === quoteContactName)
      );
    });

    setShareItem({
      id: quote.id,
      type: "quote",
      name: `${quote.quoteNumber || quote.quote_number || quote.id} - ${quote.title || "Devis"}`,
      items: [{ type: "quote", id: quote.id }],
    });
    setShareDefaultProspect(linkedProspect || null);
    setShareOpen(true);
  };

  const clearQuoteDragState = useCallback(() => {
    if (quoteDragTimerRef.current) {
      window.clearTimeout(quoteDragTimerRef.current);
      quoteDragTimerRef.current = null;
    }
    quoteTouchDragRef.current = {
      active: false,
      quote: null,
      pointerId: null,
      startX: 0,
      startY: 0,
    };
    document.body.style.userSelect = "";
    setDraggedQuote(null);
    setQuoteDragOverColumn("");
  }, []);

  const resolveQuoteDropColumn = useCallback((clientX, clientY) => {
    const target = document
      .elementFromPoint(clientX, clientY)
      ?.closest?.("[data-quote-drop-column]");
    const column = target?.dataset?.quoteDropColumn || "";
    return QUOTE_DROP_COLUMNS.has(column) ? column : "";
  }, []);

  const commitQuoteDrop = useCallback((quote, nextColumn) => {
    if (
      !quote?.id
      || !QUOTE_DROP_COLUMNS.has(nextColumn)
      || quoteColumnId(quote) === nextColumn
    ) {
      clearQuoteDragState();
      return;
    }

    const requirement = quoteDropRequirement(quote, nextColumn, isAdmin);
    clearQuoteDragState();
    if (!requirement) return;
    setQuoteDropAction({ quote, nextColumn, requirement });
  }, [clearQuoteDragState, isAdmin]);

  const handleQuoteDragStart = useCallback((event, quote) => {
    if (event.target.closest("button, a, input, select, textarea")) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", quote.id);
    setDraggedQuote(quote);
  }, []);

  const handleQuoteDragOver = useCallback((event, columnId) => {
    if (!draggedQuote || !QUOTE_DROP_COLUMNS.has(columnId)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setQuoteDragOverColumn(columnId);
  }, [draggedQuote]);

  const handleQuoteDrop = useCallback((event, columnId) => {
    event.preventDefault();
    commitQuoteDrop(draggedQuote, columnId);
  }, [commitQuoteDrop, draggedQuote]);

  const activateQuotePointerDrag = useCallback((current, x, y) => {
    if (current.active) return;
    current.active = true;
    document.body.style.userSelect = "none";
    setDraggedQuote(current.quote);
    setQuoteDragOverColumn(resolveQuoteDropColumn(x, y));
    navigator.vibrate?.(25);
  }, [resolveQuoteDropColumn]);

  const handleQuotePointerDown = useCallback((event, quote) => {
    if (event.button > 0 || event.target.closest("button, a, input, select, textarea")) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const pointerType = event.pointerType || "mouse";
    quoteTouchDragRef.current = {
      active: false,
      quote,
      pointerId: event.pointerId,
      pointerType,
      startX: event.clientX,
      startY: event.clientY,
    };
    if (pointerType !== "mouse") {
      const pointerId = event.pointerId;
      quoteDragTimerRef.current = window.setTimeout(() => {
        const current = quoteTouchDragRef.current;
        if (current.pointerId === pointerId) {
          activateQuotePointerDrag(current, event.clientX, event.clientY);
        }
      }, QUOTE_TOUCH_DRAG_DELAY);
    }
  }, [activateQuotePointerDrag]);

  const handleQuotePointerMove = useCallback((event) => {
    const current = quoteTouchDragRef.current;
    if (current.pointerId !== event.pointerId) return false;
    const distance = Math.hypot(event.clientX-current.startX,event.clientY-current.startY);
    if (!current.active) {
      if (current.pointerType === "mouse" && distance >= 5) {
        activateQuotePointerDrag(current,event.clientX,event.clientY);
      } else if (distance > QUOTE_TOUCH_MOVE_TOLERANCE) {
        if (quoteDragTimerRef.current) window.clearTimeout(quoteDragTimerRef.current);
        quoteDragTimerRef.current = null;
        return false;
      }
    }
    if (!current.active) return false;
    event.preventDefault();
    if (event.clientY < 90) window.scrollBy(0,-28);
    else if (event.clientY > window.innerHeight-90) window.scrollBy(0,28);
    setQuoteDragOverColumn(resolveQuoteDropColumn(event.clientX,event.clientY));
    return true;
  }, [activateQuotePointerDrag, resolveQuoteDropColumn]);

  const handleQuotePointerUp = useCallback((event) => {
    const current = quoteTouchDragRef.current;
    if (current.pointerId !== event.pointerId) return false;
    const active = Boolean(current.active);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!active) { clearQuoteDragState(); return false; }
    event.preventDefault();
    document.body.style.userSelect = "";
    commitQuoteDrop(current.quote,resolveQuoteDropColumn(event.clientX,event.clientY));
    return true;
  }, [clearQuoteDragState, commitQuoteDrop, resolveQuoteDropColumn]);

  const closeQuoteDropAction = () => {
    if (decisionSubmitting) return;
    setQuoteDropAction(null);
  };

  const confirmQuoteDropAction = async () => {
    const action = quoteDropAction;
    if (!action) return;
    setQuoteDropAction(null);

    if (action.requirement.actionType === "submit") {
      await runAction("Devis soumis à validation.", () => quotesApi.submit(action.quote.id));
      return;
    }
    if (action.requirement.actionType === "approve") {
      await runAction("Devis validé.", () => quotesApi.approve(action.quote.id));
      return;
    }
    if (action.requirement.actionType === "share") {
      openQuoteShare(action.quote);
      return;
    }
    if (action.requirement.actionType === "accept") {
      await runAction("Devis accepté.", () => quotesApi.accept(action.quote.id));
      return;
    }
    if (action.requirement.actionType === "admin_reject") {
      openDecisionModal(action.quote, "admin_reject");
      return;
    }
    if (action.requirement.actionType === "client_refuse") {
      openDecisionModal(action.quote, "client_refuse");
      return;
    }
    if (action.requirement.actionType === "financing_refuse") {
      openDecisionModal(action.quote, "financing_refuse");
      return;
    }
    if (action.requirement.actionType === "edit") {
      openEditWorkflowQuote(action.quote);
    }
  };

  return (
    <>
      <main
        className="w-full space-y-4 px-4 py-4 sm:px-5 lg:px-6 commerce-quotes-pipeline-design commerce-quotes-pipeline-aligned acta-standard-page acta-commerce-page-standard"
        data-testid="commerce-quotes-page"
      >
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <button
            type="button"
            onClick={() => setQuoteFormOpen(true)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--do-accent)] px-5 py-3 text-sm font-black text-[var(--do-on-accent)] shadow-sm hover:bg-[var(--do-accent-strong)] sm:w-auto"
            data-testid="quote-open-create-action"
          >
            <PlusCircle size={18} weight="bold" />
            Nouveau devis
          </button>
        </header>

        <section
          className="grid grid-cols-[repeat(auto-fit,minmax(min(12rem,100%),1fr))] gap-2 sm:gap-4"
          data-testid="quotes-v1-metrics"
        >
          <MetricCard
            icon={FileText}
            label="Devis créés"
            value={totals.quotesCreated || 0}
            active={!statusFilter}
            onClick={() => setStatusFilter("")}
          />
          <MetricCard
            icon={PaperPlaneTilt}
            label="En attente admin"
            value={totals.quotesPendingValidation || 0}
            active={statusFilter === "pending_validation"}
            onClick={() => setStatusFilter("pending_validation")}
          />
          <MetricCard
            icon={CheckCircle}
            label="Validés à envoyer"
            value={totals.quotesApproved || 0}
            active={statusFilter === "approved"}
            onClick={() => setStatusFilter("approved")}
          />
          <MetricCard
            icon={CurrencyEur}
            label="CA provisoire"
            value={formatCurrency(totals.provisionalRevenueAmount || 0)}
            active={statusFilter === "accepted"}
            onClick={() => setStatusFilter("accepted")}
          />
        </section>

        <section className="rounded-[22px] border border-[var(--do-border)] bg-[var(--do-bg-elev)] p-3 shadow-sm sm:rounded-[24px] sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl font-black text-[var(--do-ink)]">
                Colonnes des devis
              </h2>
              <p className="mt-1 text-xs text-[var(--do-muted)] sm:text-sm">
                <span className="sm:hidden">
                  Consultez et traitez chaque étape du workflow.
                </span>
                <span className="hidden sm:inline">
                  Brouillon → validation admin → partagé client → accepté → financement en attente.
                </span>
              </p>
            </div>

            <div className="flex w-full min-w-0 flex-[1_1_28rem] flex-wrap items-center gap-2">
              <label className="relative order-1 min-w-0 flex-[1_1_15rem]">
                <MagnifyingGlass
                  size={16}
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
                />
                <input
                  type="search"
                  value={quoteSearch}
                  onChange={(event) => setQuoteSearch(event.target.value)}
                  placeholder="Prospect, client, société, numéro..."
                  className="h-11 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] pl-10 pr-3 text-sm outline-none focus:border-[var(--do-border-strong)] sm:h-12 sm:rounded-2xl sm:pl-11 sm:pr-4"
                  data-testid="quote-client-search"
                />
              </label>

              <div className="order-2 flex min-w-0 flex-[1_1_14rem] flex-wrap items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="h-11 min-w-0 flex-1 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)] sm:order-2 sm:h-12 sm:flex-none sm:rounded-2xl sm:px-4"
                  data-testid="quote-status-filter"
                >
                  <option value="">Tous les statuts</option>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>

                <div
                  className="shrink-0 rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-ink)] sm:order-1 sm:rounded-2xl sm:px-4 sm:py-3 sm:text-sm"
                  data-testid="quote-global-count"
                >
                  {filteredQuotes.length} devis
                </div>
              </div>
            </div>
          </div>

          {loading && (
            <div className="mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-sm text-[var(--do-muted)]">
              Chargement des devis...
            </div>
          )}

          {!loading && filteredQuotes.length === 0 && (
            <div className="mt-4 rounded-2xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] p-6 text-sm text-[var(--do-muted)]">
              Aucun devis pour le moment.
            </div>
          )}

          {!loading && filteredQuotes.length > 0 && (
            <div
              className="mt-4 grid w-full grid-cols-[repeat(auto-fit,minmax(min(14rem,100%),1fr))] gap-3"
              aria-label="Colonnes des devis commerciaux"
              data-testid="quote-kanban-board"
            >
              {groupedQuotes.map((column) => (
                <section
                  key={column.id}
                  className={`flex min-w-0 flex-col rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-2.5 transition sm:rounded-2xl ${
                    openMobileColumn === column.id ? "" : "max-md:p-0"
                  } ${
                    draggedQuote && quoteColumnId(draggedQuote) === column.id
                      ? "ring-2 ring-[var(--do-accent)]"
                      : quoteDragOverColumn === column.id
                        ? quoteDropRequirement(draggedQuote, column.id, isAdmin)?.blocked
                          ? "ring-2 ring-[var(--do-accent)] ring-offset-2"
                          : "ring-2 ring-[var(--do-accent)]0 ring-offset-2"
                        : draggedQuote
                          ? quoteDropRequirement(draggedQuote, column.id, isAdmin)?.blocked
                            ? "ring-1 ring-[var(--do-accent)]"
                            : "ring-1 ring-[var(--do-accent)]"
                          : ""
                  }`}
                  data-testid={`quote-kanban-column-${column.id}`}
                  data-quote-drop-column={column.id}
                  onDragEnter={(event) => {
                    if (draggedQuote) {
                      event.preventDefault();
                      setQuoteDragOverColumn(column.id);
                    }
                  }}
                  onDragOver={(event) => handleQuoteDragOver(event, column.id)}
                  onDrop={(event) => handleQuoteDrop(event, column.id)}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenMobileColumn((current) =>
                        current === column.id ? "" : column.id
                      )
                    }
                    className={`flex w-full items-start justify-between gap-3 text-left md:cursor-default ${
                      openMobileColumn === column.id
                        ? "mb-3"
                        : "max-md:px-3 max-md:py-2"
                    }`}
                    aria-expanded={openMobileColumn === column.id}
                    data-testid={`quote-mobile-toggle-${column.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-2.5 w-2.5 shrink-0 rounded-full ${column.dot}`}
                        />

                        <h3 className="truncate font-heading text-sm font-black leading-tight text-[var(--do-ink)]">
                          {column.title}
                        </h3>
                      </div>

                      <p
                        className={`mt-1 truncate text-xs text-[var(--do-muted)] ${
                          openMobileColumn === column.id
                            ? "block"
                            : "hidden md:block"
                        }`}
                      >
                        {column.description}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <span className="rounded-full bg-[var(--do-bg-solid)] px-2.5 py-1 text-xs font-black text-[var(--do-muted)]">
                        {column.quotes.length}
                      </span>

                      <CaretDown
                        size={15}
                        weight="bold"
                        className={`mt-1 transition-transform md:hidden ${
                          openMobileColumn === column.id ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </button>

                  <div
                    className={`flex flex-1 flex-col gap-3 ${
                      openMobileColumn === column.id
                        ? "flex"
                        : "hidden md:flex"
                    }`}
                  >
                    {draggedQuote && (
                      <div
                        className={`rounded-xl border px-3 py-2 text-center text-xs font-black ${
                          quoteDropRequirement(draggedQuote, column.id, isAdmin)?.blocked
                            ? "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                            : "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                        }`}
                        data-testid={`quote-drop-hint-${column.id}`}
                      >
                        {quoteDropRequirement(draggedQuote, column.id, isAdmin)?.blocked
                          ? "Déposer pour voir l’action requise"
                          : "Déposer ici"}
                      </div>
                    )}
                    {column.quotes.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-elev)] p-4 text-xs text-[var(--do-muted)]">
                        Aucun devis
                      </div>
                    )}

                    {column.visibleQuotes.map((quote) => (
                      <article
                        key={quote.id}
                        className={`select-none rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3 shadow-sm transition ${
                          draggedQuote?.id === quote.id
                            ? "touch-none cursor-grabbing opacity-55 ring-2 ring-[var(--do-accent)]"
                            : "touch-pan-y cursor-grab hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing"
                        }`}
                        data-testid="quote-kanban-card"
                        data-quote-card-draggable="true"
                        draggable={false}
                        onDragStart={(event) => handleQuoteDragStart(event, quote)}
                        onDragEnd={clearQuoteDragState}
                        onPointerDown={(event) => handleQuotePointerDown(event, quote)}
                        onPointerMove={handleQuotePointerMove}
                        onPointerUp={handleQuotePointerUp}
                        onPointerCancel={clearQuoteDragState}
                        aria-grabbed={draggedQuote?.id === quote.id}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-xs font-black uppercase tracking-[0.12em] text-[var(--do-accent)]">
                              {quote.quoteNumber || quote.quote_number || quote.id}
                            </div>

                            <h4 className="mt-1 line-clamp-2 font-heading text-sm font-black leading-snug text-[var(--do-ink)]">
                              {quote.title || "Devis commercial"}
                            </h4>

                            <p className="mt-1 truncate text-xs text-[var(--do-muted)]">
                              {quote.customerName || quote.prospectName || quote.clientName || quote.company || "Client non lié"}
                            </p>

                            {(quote.machineName || quote.machine_name) && (
                              <p className="mt-1 truncate text-xs text-[var(--do-muted)]">
                                {quote.machineName || quote.machine_name}
                              </p>
                            )}
                          </div>

                          <div className="shrink-0 text-right font-heading text-sm font-black text-[var(--do-ink)]">
                            {formatCurrency(quote.amount)}
                          </div>
                        </div>

                        <div className="mt-3">
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-black ${statusClass(quote.status)}`}>
                            {STATUS_LABELS[quote.status] || quote.status}
                          </span>
                        </div>

                        {quote.status === "accepted" &&
                          quote.legacyStatus !== "purchase_order_created" &&
                          quoteFinancingStatus(quote) === "pending" && (
                          <div
                            className="mt-2 flex flex-wrap gap-1.5"
                            data-testid="quote-financing-pending-state"
                          >
                            <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                              CA provisoire
                            </span>
                            <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                              Financement en attente
                            </span>
                          </div>
                        )}

                        {quote.status === "accepted" &&
                          quoteFinancingStatus(quote) === "approved" && (
                          <div
                            className="mt-2 flex flex-wrap gap-1.5"
                            data-testid="quote-financing-approved-state"
                          >
                            <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                              CA confirmé
                            </span>
                            <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                              Financement accepté
                            </span>
                            {quote.gmaoStatus === "ready_for_transfer" && (
                              <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                                Transfert GMAO à préparer
                              </span>
                            )}
                            {quote.gmaoStatus === "transferred" && (
                              <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                                Transféré dans la GMAO
                              </span>
                            )}
                            {quote.gmaoStatus === "not_applicable" && (
                              <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                                Sans transfert GMAO
                              </span>
                            )}
                          </div>
                        )}

                        {quote.status === "accepted" &&
                          quoteFinancingStatus(quote) === "refused" && (
                          <div
                            className="mt-2 flex flex-wrap gap-1.5"
                            data-testid="quote-financing-refused-state"
                          >
                            <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                              Financement refusé
                            </span>
                            <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                              CA non confirmé
                            </span>
                          </div>
                        )}

                        {quote.status === "accepted" &&
                          quoteFinancingStatus(quote) === "cancelled" && (
                          <div
                            className="mt-2 flex flex-wrap gap-1.5"
                            data-testid="quote-financing-cancelled-state"
                          >
                            <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-bg-2)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                              Financement annulé
                            </span>
                            <span className="inline-flex rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-1 text-[11px] font-black text-[var(--do-ink)]">
                              CA non confirmé
                            </span>
                          </div>
                        )}

                        {quote.rejectionReason && (
                          <div className="mt-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs text-[var(--do-accent)]">
                            Motif admin : {quote.rejectionReason}
                          </div>
                        )}

                        {quote.refusalReason && (
                          <div className="mt-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs text-[var(--do-accent)]">
                            Refus client : {quote.refusalReason}
                          </div>
                        )}

                        <div className="mt-3 flex flex-wrap gap-1.5 border-t border-[var(--do-border)] pt-3">
                          {(
                            PDF_ALLOWED_STATUSES.has(quote.status)
                            || ["draft", "rejected", "pending_validation"].includes(quote.status)
                          ) && (
                            <button
                              type="button"
                              onClick={() => previewQuotePdf(quote)}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-2 text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-solid)]"
                              data-testid="quote-preview-pdf-action"
                            >
                              <FileText size={15} weight="bold" />
                              Aperçu
                            </button>
                          )}

                          {PDF_ALLOWED_STATUSES.has(quote.status) && (
                            <button
                              type="button"
                              onClick={() => downloadQuotePdf(quote)}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-2 text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-solid)]"
                              data-testid="quote-download-pdf-action"
                            >
                              <FileText size={15} weight="bold" />
                              PDF
                            </button>
                          )}

                          {canEditWorkflowQuote(quote) && (
                            <button
                              type="button"
                              onClick={() => openEditWorkflowQuote(quote)}
                              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-accent-soft)]"
                              data-testid="quote-edit-workflow-action"
                            >
                              Modifier
                            </button>
                          )}

                          {(quote.status === "draft" || quote.status === "rejected") && (
                            <button
                              type="button"
                              onClick={() =>
                                runAction(
                                  "Devis soumis à validation.",
                                  () => quotesApi.submit(quote.id)
                                )
                              }
                              className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                              data-testid="quote-submit-action"
                            >
                              <PaperPlaneTilt size={15} weight="bold" />
                              Demander validation
                            </button>
                          )}

                          {isAdmin && quote.status === "pending_validation" && (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  runAction(
                                    "Devis validé.",
                                    () => quotesApi.approve(quote.id)
                                  )
                                }
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                                data-testid="quote-approve-action"
                              >
                                <CheckCircle size={15} weight="bold" />
                                Valider
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  openDecisionModal(quote, "admin_reject")
                                }
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                                data-testid="quote-reject-action"
                              >
                                <XCircle size={15} weight="bold" />
                                Rejeter
                              </button>
                            </>
                          )}

                          {quote.status === "approved" && (
                            <button
                              type="button"
                              onClick={() => openQuoteShare(quote)}
                              className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                              data-testid="quote-send-action"
                            >
                              <PaperPlaneTilt size={15} weight="bold" />
                              Partager au client
                            </button>
                          )}

                          {isAdmin &&
                            quote.status === "accepted" &&
                            quote.legacyStatus !== "purchase_order_created" &&
                            quoteFinancingStatus(quote) === "pending" && (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  openDecisionModal(
                                    quote,
                                    "financing_approve"
                                  )
                                }
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                                data-testid="quote-financing-approve-action"
                              >
                                <CheckCircle size={15} weight="bold" />
                                Valider financement
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  openDecisionModal(
                                    quote,
                                    "financing_refuse"
                                  )
                                }
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                                data-testid="quote-financing-refuse-action"
                              >
                                Refuser financement
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  openDecisionModal(
                                    quote,
                                    "financing_cancel"
                                  )
                                }
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-2 text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-2)]"
                                data-testid="quote-financing-cancel-action"
                              >
                                Annuler dossier
                              </button>
                            </>
                          )}

                          {quote.status === "sent" && (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  runAction(
                                    "Devis accepté.",
                                    () => quotesApi.accept(quote.id)
                                  )
                                }
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                                data-testid="quote-accept-action"
                              >
                                Accepté
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  openDecisionModal(quote, "client_refuse")
                                }
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                                data-testid="quote-refuse-action"
                              >
                                Refusé
                              </button>
                            </>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>

                  {column.quotes.length > QUOTE_COLUMN_PAGE_SIZE && (
                    <div
                      className={`mt-3 items-center justify-between gap-3 border-t border-[var(--do-border)] pt-3 ${
                        openMobileColumn === column.id
                          ? "flex"
                          : "hidden md:flex"
                      }`}
                    >
                      <span className="text-xs font-semibold text-[var(--do-muted)]">
                        {column.startIndex + 1}-{column.endIndex} sur {column.quotes.length}
                      </span>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => updateColumnPage(column.id, -1)}
                          disabled={column.page <= 1}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-sm font-black text-[var(--do-ink)] disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label={`Page précédente ${column.title}`}
                        >
                          ‹
                        </button>

                        <span className="text-xs font-black text-[var(--do-muted)]">
                          {column.page}/{column.totalPages}
                        </span>

                        <button
                          type="button"
                          onClick={() => updateColumnPage(column.id, 1)}
                          disabled={column.page >= column.totalPages}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-sm font-black text-[var(--do-ink)] disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label={`Page suivante ${column.title}`}
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}
        </section>

        {quoteFormOpen && (
          <div
            className="acta-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
            data-testid="quote-form-modal"
            onClick={() => setQuoteFormOpen(false)}
          >
            <div
              className="acta-modal-surface acta-modal-scroll max-h-[92dvh] w-full max-w-3xl rounded-3xl p-4 sm:p-5"
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">
                    Nouveau devis
                  </h2>

                  <p className="mt-1 text-xs text-[var(--do-muted)]">
                    Le numéro, le titre et le vendeur sont générés automatiquement.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setQuoteFormOpen(false)
                  }
                  className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-muted)] hover:bg-[var(--do-bg-solid)]"
                >
                  Fermer
                </button>
              </div>

              <QuoteCreateForm
                onCancel={() =>
                  setQuoteFormOpen(false)
                }
                onCreated={async () => {
                  setQuoteFormOpen(false);
                  await load();
                }}
              />
            </div>
          </div>
        )}
      </main>

      {editingQuote && (
        <div
          className="acta-modal-overlay fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4"
          data-testid="quote-edit-modal"
          onClick={closeEditWorkflowQuote}
        >
          <form
            onSubmit={submitEditWorkflowQuote}
            className="acta-modal-surface acta-modal-scroll max-h-[90dvh] w-full max-w-2xl rounded-3xl p-5 sm:p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-[var(--do-border)] pb-4">
              <div>
                <div className="label-eyebrow">Devis rejeté</div>
                <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-ink)]">
                  Modifier le devis
                </h2>
                <p className="mt-1 text-sm text-[var(--do-muted)]">
                  Corrigez le devis avant de le soumettre à nouveau.
                </p>
              </div>

              <button
                type="button"
                onClick={closeEditWorkflowQuote}
                disabled={editSubmitting}
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-muted)] hover:bg-[var(--do-bg-solid)] disabled:opacity-50"
              >
                Fermer
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Titre du devis
                </span>
                <input
                  value={editForm.title}
                  onChange={(event) =>
                    updateEditForm("title", event.target.value)
                  }
                  className="mt-1 w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)]"
                  data-testid="quote-edit-title"
                  autoFocus
                />
              </label>

              <label className="block">
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Produit / prestation
                </span>
                <input
                  value={editForm.machine_name}
                  onChange={(event) =>
                    updateEditForm("machine_name", event.target.value)
                  }
                  className="mt-1 w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)]"
                  data-testid="quote-edit-machine"
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-bold text-[var(--do-muted)]">
                    Quantité
                  </span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={editForm.quantity}
                    onChange={(event) =>
                      updateEditForm("quantity", event.target.value)
                    }
                    className="mt-1 w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)]"
                    data-testid="quote-edit-quantity"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-bold text-[var(--do-muted)]">
                    Montant HT
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(event) =>
                      updateEditForm("amount", event.target.value)
                    }
                    className="mt-1 w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)]"
                    data-testid="quote-edit-amount"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-xs font-bold text-[var(--do-muted)]">
                  Notes internes
                </span>
                <textarea
                  rows={4}
                  value={editForm.notes}
                  onChange={(event) =>
                    updateEditForm("notes", event.target.value)
                  }
                  className="mt-1 w-full resize-y rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)]"
                  data-testid="quote-edit-notes"
                />
              </label>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 border-t border-[var(--do-border)] pt-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeEditWorkflowQuote}
                disabled={editSubmitting}
                className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 py-3 text-sm font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-solid)] disabled:opacity-50"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={editSubmitting}
                className="rounded-2xl bg-[var(--do-accent)] px-5 py-3 text-sm font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="quote-edit-submit"
              >
                {editSubmitting ? "Enregistrement…" : "Enregistrer les modifications"}
              </button>
            </div>
          </form>
        </div>
      )}

      {decisionModal && (
        <div
          className="acta-modal-overlay fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4"
          data-testid="quote-decision-modal"
          onClick={closeDecisionModal}
        >
          <form
            onSubmit={submitDecision}
            className="acta-modal-surface acta-modal-scroll max-h-[92dvh] w-full max-w-lg rounded-3xl p-5 sm:p-6"
            onClick={(event) => event.stopPropagation()}
          >
            {(() => {
              const type =
                decisionModal.type;
              const isApproval =
                type ===
                "financing_approve";
              const isFinancing =
                type.startsWith(
                  "financing_"
                );
              const titleByType = {
                admin_reject:
                  "Rejeter le devis",
                client_refuse:
                  "Enregistrer le refus client",
                financing_approve:
                  "Valider le financement",
                financing_refuse:
                  "Refuser le financement",
                financing_cancel:
                  "Annuler le dossier de financement",
              };
              const placeholderByType = {
                admin_reject:
                  "Expliquez les corrections attendues…",
                client_refuse:
                  "Indiquez la raison communiquée par le client…",
                financing_approve:
                  "Référence, organisme ou précision facultative…",
                financing_refuse:
                  "Indiquez le motif du refus de financement…",
                financing_cancel:
                  "Indiquez pourquoi le dossier est annulé…",
              };

              return (
                <>
                  <div className="flex items-start gap-4">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                        isApproval
                          ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                          : "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                      }`}
                    >
                      {isApproval ? (
                        <CheckCircle
                          size={22}
                          weight="bold"
                        />
                      ) : (
                        <XCircle
                          size={22}
                          weight="bold"
                        />
                      )}
                    </div>

                    <div>
                      <div className="label-eyebrow">
                        {isFinancing
                          ? "Décision financière"
                          : "Confirmation"}
                      </div>
                      <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-ink)]">
                        {titleByType[type]}
                      </h2>
                      <p className="mt-1 text-sm text-[var(--do-muted)]">
                        {decisionModal.quote.quoteNumber ||
                          decisionModal.quote.quote_number ||
                          decisionModal.quote.id}
                        {" — "}
                        {decisionModal.quote.title ||
                          "Devis commercial"}
                      </p>
                    </div>
                  </div>

                  {isApproval && (
                    <div
                      className="mt-5 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4"
                      data-testid="quote-financing-gmao-choice"
                    >
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          checked={
                            decisionPrepareGmao
                          }
                          onChange={(event) =>
                            setDecisionPrepareGmao(
                              event.target.checked
                            )
                          }
                          className="mt-1 h-4 w-4"
                        />
                        <span>
                          <span className="block text-sm font-black text-[var(--do-ink)]">
                            Préparer le transfert GMAO
                          </span>
                          <span className="mt-1 block text-xs leading-5 text-[var(--do-ink)]">
                            À conserver pour une machine ou un équipement.
                            Décochez pour une prestation sans installation.
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  <label className="mt-5 block">
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      {isApproval
                        ? "Précision facultative"
                        : "Motif obligatoire"}
                    </span>

                    <textarea
                      rows={4}
                      value={decisionReason}
                      onChange={(event) =>
                        setDecisionReason(
                          event.target.value
                        )
                      }
                      placeholder={
                        placeholderByType[
                          type
                        ]
                      }
                      className={`mt-2 w-full resize-y rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none ${
                        isApproval
                          ? "focus:border-[var(--do-border-strong)]"
                          : "focus:border-[var(--do-border-strong)]"
                      }`}
                      data-testid="quote-decision-reason"
                      autoFocus={
                        !isApproval
                      }
                    />
                  </label>

                  {isApproval && (
                    <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-xs leading-5 text-[var(--do-ink)]">
                      Cette validation confirme le chiffre d’affaires.
                      Le transfert GMAO reste une étape séparée :
                      la machine sera créée depuis la file
                      « Installations à préparer ».
                    </div>
                  )}

                  <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={
                        closeDecisionModal
                      }
                      disabled={
                        decisionSubmitting
                      }
                      className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 py-3 text-sm font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-solid)] disabled:opacity-50"
                    >
                      Annuler
                    </button>

                    <button
                      type="submit"
                      disabled={
                        decisionSubmitting ||
                        (
                          !isApproval &&
                          !decisionReason.trim()
                        )
                      }
                      className={`rounded-2xl px-5 py-3 text-sm font-black text-[var(--do-on-accent)] disabled:cursor-not-allowed disabled:opacity-50 ${
                        isApproval
                          ? "bg-[var(--do-accent)] hover:bg-[var(--do-accent-strong)]"
                          : "bg-[var(--do-accent)] hover:bg-[var(--do-accent-strong)]"
                      }`}
                      data-testid="quote-decision-submit"
                    >
                      {decisionSubmitting
                        ? "Enregistrement…"
                        : isApproval
                          ? "Confirmer le financement"
                          : type === "admin_reject"
                            ? "Confirmer le rejet"
                            : type === "client_refuse"
                              ? "Confirmer le refus"
                              : type === "financing_cancel"
                                ? "Confirmer l’annulation"
                                : "Confirmer le refus"}
                    </button>
                  </div>
                </>
              );
            })()}
          </form>
        </div>
      )}

      <QuoteDropActionModal
        action={quoteDropAction}
        saving={decisionSubmitting}
        onClose={closeQuoteDropAction}
        onConfirm={confirmQuoteDropAction}
        onPreview={() => {
          if (quoteDropAction?.quote) previewQuotePdf(quoteDropAction.quote);
        }}
      />

      {quotePreview && (
        <div
          className="acta-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          data-testid="quote-preview-modal"
          onClick={closeQuotePreview}
        >
          <div
            className="flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-[var(--do-border)] px-5 py-4">
              <div className="min-w-0">
                <div className="label-eyebrow">Prévisualisation</div>
                <h2 className="truncate font-heading text-lg font-black text-[var(--do-ink)]">
                  {quotePreview.title}
                </h2>
              </div>

              <button
                type="button"
                onClick={closeQuotePreview}
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-muted)] hover:bg-[var(--do-bg-solid)]"
              >
                Fermer
              </button>
            </div>

            <iframe
              src={quotePreview.url}
              title={quotePreview.title}
              className="h-full w-full bg-[var(--do-bg-solid)]"
            />
          </div>
        </div>
      )}

      {shareOpen && shareItem && (
        <ShareModal
          item={shareItem}
          defaultProspect={shareDefaultProspect}
          createShare={(payload) => quotesApi.share(shareItem.id, payload)}
          onGenerated={() => load()}
          onClose={() => {
            setShareOpen(false);
            setShareItem(null);
            setShareDefaultProspect(null);
            load();
          }}
        />
      )}
    </>
  );
}

// ACTA_AFFAIRES_LEGACY_QUOTES_REDIRECT_V17
export default function CommerceQuotes() {
  return <Navigate to="/app/commerce/pipeline" replace />;
}
